// DIAGNOSTIC — molette/trackpad sans effet sur le canvas chez certains
// utilisateurs Windows (flèches OK), non reproduit en interne. Activé seulement
// par `?wheel_diag=1` dans l'URL : enregistre les événements wheel (arrivent-ils,
// sont-ils annulés, sur quel élément), les scroll natifs (quel élément défile
// réellement), les flèches (comparaison) et toute écriture programmatique de la
// position du scroller (avec la pile d'appel). L'utilisateur télécharge le
// relevé en JSON via le bouton affiché par InfinitePage. À retirer une fois la
// cause établie.
import { getUrlParams } from '../utils/browser';
import { downloadBlob } from '../utils/downloadBlob';

const MAX_ENTRIES = 5000;
const entries = [];

export const isWheelDiagnosticEnabled = () => getUrlParams('wheel_diag') === '1';

const now = () => Math.round(performance.now());

const describe = (node) => {
  if (!(node instanceof Element)) return node === document ? 'document' : String(node);
  const id = node.id ? `#${node.id}` : '';
  const className = typeof node.className === 'string' && node.className
    ? `.${node.className.trim().split(/\s+/).slice(0, 3).join('.')}`
    : '';
  return `${node.tagName.toLowerCase()}${id}${className}`.slice(0, 80);
};

const record = (entry) => {
  if (entries.length >= MAX_ENTRIES) return null;
  const stamped = { at: now(), ...entry };
  entries.push(stamped);
  return stamped;
};

const snapshot = (scroller, getContext) => ({
  userAgent: navigator.userAgent,
  platform: navigator.userAgentData?.platform ?? navigator.platform,
  devicePixelRatio: window.devicePixelRatio,
  innerWidth: window.innerWidth,
  innerHeight: window.innerHeight,
  pageOverflow: document.scrollingElement.scrollHeight - window.innerHeight,
  pageScrollTop: document.scrollingElement.scrollTop,
  scroller: scroller && {
    clientHeight: scroller.clientHeight,
    scrollHeight: scroller.scrollHeight,
    offsetWidth: scroller.offsetWidth,
    clientWidth: scroller.clientWidth,
  },
  ...getContext(),
});

export const installWheelScrollDiagnostic = (scroller, getContext) => {
  const desc = Object.getOwnPropertyDescriptor(Element.prototype, 'scrollTop');
  const top = () => Math.round(desc.get.call(scroller));
  const stack = () => new Error().stack.split('\n').slice(3, 8).map((l) => l.trim());
  const wheelEntries = new WeakMap();
  const originals = {};

  record({ type: 'install', env: snapshot(scroller, getContext) });

  Object.defineProperty(scroller, 'scrollTop', {
    configurable: true,
    get() {
      return desc.get.call(this);
    },
    set(value) {
      record({ type: 'write', from: top(), to: value, stack: stack() });
      desc.set.call(this, value);
    },
  });
  ['scrollBy', 'scroll', 'scrollTo'].forEach((method) => {
    originals[method] = scroller[method];
    scroller[method] = (...args) => {
      record({ type: method, from: top(), args: JSON.stringify(args), stack: stack() });
      return originals[method].apply(scroller, args);
    };
  });

  // Capture : on voit l'événement avant tout handler (il arrive bien jusqu'à
  // la page). Bubble : defaultPrevented reflète ce qu'ont fait l'app, les libs
  // et les extensions.
  const onWheelCapture = (e) => {
    const hit = document.elementFromPoint(e.clientX, e.clientY);
    const entry = record({
      type: 'wheel',
      deltaX: e.deltaX,
      deltaY: e.deltaY,
      deltaMode: e.deltaMode,
      ctrlKey: e.ctrlKey,
      target: describe(e.target),
      hit: describe(hit),
      inScroller: e.target instanceof Node && scroller.contains(e.target),
      top: top(),
    });
    if (entry) wheelEntries.set(e, entry);
  };
  const onWheelBubble = (e) => {
    const entry = wheelEntries.get(e);
    if (entry) entry.prevented = e.defaultPrevented;
  };
  // Tout élément qui défile (capture : scroll ne remonte pas) : révèle si la
  // molette fait défiler autre chose que le scroller du document.
  const onScroll = (e) =>
    record({
      type: 'scroll',
      target: e.target === scroller ? 'scroller' : describe(e.target),
      top: e.target === scroller ? top() : e.target.scrollTop ?? document.scrollingElement.scrollTop,
    });
  const onKeyDown = (e) => {
    if (!['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown'].includes(e.key)) return;
    record({ type: 'key', key: e.key, target: describe(e.target), top: top() });
  };

  window.addEventListener('wheel', onWheelCapture, { capture: true, passive: true });
  window.addEventListener('wheel', onWheelBubble, { passive: true });
  document.addEventListener('scroll', onScroll, { capture: true, passive: true });
  window.addEventListener('keydown', onKeyDown, true);

  window.__wheelDiag = { entries, download: () => downloadWheelDiagnostic(scroller, getContext) };

  return () => {
    window.removeEventListener('wheel', onWheelCapture, { capture: true });
    window.removeEventListener('wheel', onWheelBubble);
    document.removeEventListener('scroll', onScroll, { capture: true });
    window.removeEventListener('keydown', onKeyDown, true);
    delete scroller.scrollTop;
    Object.keys(originals).forEach((method) => {
      delete scroller[method];
    });
    record({ type: 'uninstall' });
  };
};

export const downloadWheelDiagnostic = (scroller, getContext) => {
  const report = { env: snapshot(scroller, getContext), entries };
  const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
  downloadBlob(blob, `wheel-diagnostic-${new Date().toISOString().replace(/[:.]/g, '-')}.json`);
};
